import { Transformer } from 'modules/Canvas/components/Canvas/Transformer';
import { StageContext } from 'modules/Canvas/contexts/StageContext';
import { getBoundingBoxData } from 'modules/Canvas/components/Canvas/helpers/shapes';
import { useMediaState } from 'modules/Canvas/contexts/MediaProvider/MediaState';
import { getNodeCoords, getNodeDimensions, hasBoxExceeded } from 'utils/konva/drawingHelpers';
import Konva from 'konva';
import { KonvaNodeEvents } from 'react-konva';
import { useCanvasStore } from 'modules/Canvas/contexts/CanvasStoreContext';
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { KonvaEventObject } from 'konva/types/Node';
import { observer } from 'mobx-react-lite';
import { baseTheme } from 'styles/utils/core';
import { setCursor } from 'components/Canvas/helpers';

const MIN_CANVAS_DIM = 5;
const redraw = (evt: KonvaEventObject<Event>): void => evt.target?.getLayer()?.batchDraw();

export const TransformLayer = observer(
  ({
    elementRef,
    update,
    onMouseDown,
    onMouseUp,
    shouldOverdrawWholeArea,
  }: {
    elementRef: React.RefObject<Konva.Rect>;
    update: (b: CF.API.BoundingBox) => Promise<void | string> | void | null;
    onMouseDown?: () => void;
    onMouseUp?: () => void;
    shouldOverdrawWholeArea?: boolean;
  }) => {
    const prevPosRef = useRef<{ x: number; y: number; width: number; height: number }>(
      elementRef.current
        ? Object.assign(getNodeDimensions(elementRef.current), getNodeCoords(elementRef.current))
        : { x: 0, y: 0, width: 0, height: 0 },
    );
    const canvasStore = useCanvasStore();
    const transformerRef = useRef<Konva.Transformer>(null);
    const { container } = useContext(StageContext);
    const containerDimensions = Object.assign(getNodeDimensions(container), getNodeCoords(container));

    // attach
    useEffect(() => {
      // we need to attach transformer manually over time
      const element = elementRef.current;
      if (element) {
        transformerRef.current?.nodes([element]);
        transformerRef.current?.getLayer()?.batchDraw();
      } else {
        canvasStore.setSelectedShape(null);
      }
    }, [elementRef]);

    const onDragMove = useCallback(() => {
      if (!elementRef.current) return null;
      const rect = elementRef.current;
      const coords = Object.assign(getNodeDimensions(rect), getNodeCoords(rect));

      const hasExceeded = hasBoxExceeded(
        coords,
        {
          ...containerDimensions,
          ...getNodeCoords(container),
        },
        0,
      );

      if (hasExceeded) {
        const xAxisMax = containerDimensions.width - rect.width();
        const yAxisMax = containerDimensions.height - rect.height();

        const ds = { x: { min: 0, max: xAxisMax }, y: { min: 0, max: yAxisMax } };
        const boundedCoords = (
          Object.entries(ds) as unknown as [
            'x' | 'y',
            {
              min: number;
              max: number;
            },
          ][]
        ).reduce(
          (acc, currVal) => {
            // if (coords.x < min) { coords.x = min } {}
            const [key, { min, max }] = currVal;
            if (coords[key] < min) acc[key] = min;
            if (coords[key] > max) acc[key] = max;
            return acc;
          },
          { x: coords.x, y: coords.y } as Record<'x' | 'y', number>,
        );

        rect.position(boundedCoords);
      }

      prevPosRef.current = Object.assign(prevPosRef.current, Object.assign(getNodeDimensions(rect), getNodeCoords(rect)));
      return null;
    }, []);

    const onTransform: (evt: KonvaEventObject<Event>) => void = useCallback(
      (evt) => {
        const rect = evt.target as Konva.Rect;
        if (!rect) return null;
        const scaleX = rect.x() < -1 ? 1 : rect.scaleX();
        const scaleY = rect.y() < -1 ? 1 : rect.scaleY();
        const coords = {
          x: rect.x() < 0 ? 0 : rect.x(),
          y: rect.y() < 0 ? 0 : rect.y(),
        };
        const width = rect.width() * scaleX;
        const height = rect.height() * scaleY;
        const hasExceeded = hasBoxExceeded(Object.assign(coords, { width, height }), {
          ...containerDimensions,
          ...getNodeCoords(container),
        });

        if (scaleX < 0 || scaleY < 0 || rect.rotation() < 0 || hasExceeded || width < MIN_CANVAS_DIM || height < MIN_CANVAS_DIM) {
          const prevPos = prevPosRef.current;
          // transformerRef.current.stopTransform();
          rect.scaleX(1);
          rect.scaleY(1);
          // rect.rotation(0);
          rect.position({ x: prevPos.x, y: prevPos.y });
          rect.width(prevPos.width);
          rect.height(prevPos.height);
          // rect.getLayer()?.batchDraw();
        } else {
          const allowedWidth = Math.min(Math.max(MIN_CANVAS_DIM, width), containerDimensions.width - rect.x());
          const allowedHeight = Math.min(Math.max(MIN_CANVAS_DIM, height), containerDimensions.height - rect.y());
          rect.scaleX(1);
          rect.scaleY(1);
          rect.position(coords);
          rect.width(allowedWidth);
          rect.height(allowedHeight);
          rect.getLayer()?.batchDraw();
          prevPosRef.current = Object.assign(coords, { width: allowedWidth, height: allowedHeight });
        }
        return null;
      },
      [containerDimensions, container],
    );

    const { element } = useMediaState();
    const media = element as HTMLImageElement | HTMLVideoElement;

    const eventHandlers: KonvaNodeEvents = useMemo(
      () => ({
        onTransformEnd: (evt) => {
          onTransform(evt);
          redraw(evt);
          setTimeout(() => {
            const bbox = getBoundingBoxData(prevPosRef.current, media);
            update(bbox);
          }, 10);
        },
        onDragEnd: (evt) => {
          onDragMove();
          redraw(evt);
          setTimeout(() => {
            const bbox = getBoundingBoxData(prevPosRef.current, media);
            update(bbox);
          }, 10);
        },
      }),
      [canvasStore, media, update],
    );

    const handleMouseMove = useCallback((evt: Konva.KonvaEventObject<MouseEvent>) => {
      setCursor(evt, 'pointer');
    }, []);

    const handleMouseDown = useCallback(() => {
      onMouseDown?.();
    }, [onMouseDown]);

    const handleMouseUp = useCallback(() => {
      onMouseUp?.();
    }, [onMouseUp]);

    const handleMouseLeave = useCallback((evt: Konva.KonvaEventObject<MouseEvent>) => {
      setCursor(evt, 'auto');
    }, []);

    return (
      <Transformer
        ref={transformerRef}
        keepRatio={false}
        rotateEnabled={false}
        resizeEnabled
        // avoid "flipping" the bounding box while resizing
        boundBoxFunc={(oldBoundBox, newBoundBox) => {
          if (newBoundBox.rotation < 0 || newBoundBox.width < MIN_CANVAS_DIM || newBoundBox.height < MIN_CANVAS_DIM) {
            return oldBoundBox;
          }

          return newBoundBox;
        }}
        // make sure that bbox doesn't exceed the image bounds
        onTransform={onTransform}
        onTransformEnd={eventHandlers.onTransformEnd}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onDragEnd={eventHandlers.onDragEnd}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onDragMove={onDragMove}
        borderStrokeWidth={0}
        anchorFill={baseTheme.light.colors.primary600}
        anchorStroke={baseTheme.light.colors.primary600}
        shouldOverdrawWholeArea={shouldOverdrawWholeArea}
        ignoreStroke
      />
    );
  },
);
